// src/pages/AdminManagement.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import axios from "../utils/axios";

const AdminManagement = () => {
  const { adminData, logout } = useAuth();
  const navigate = useNavigate();

  // State for admin list
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [actionLoading, setActionLoading] = useState({}); // Track which button is loading

  // State for activity log
  const [activityLog, setActivityLog] = useState([]);
  const [showActivityLog, setShowActivityLog] = useState(false);

  // State for create admin form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    is_super_admin: false,
  });
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);

  // State for change password modal
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Check if current user is super admin
  useEffect(() => {
    if (!adminData?.is_super_admin) {
      alert("Access denied. Super admin privileges required.");
      navigate("/admin-dashboard");
    } else {
      fetchAdmins();
      fetchActivityLog();
    }
  }, [adminData, navigate]);

  // Fetch all admin users
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("Fetching admins..."); // Debug log

      const response = await axios.get("/api/admin/all-admins");
      console.log("Admin response:", response.data); // Debug log

      if (response.data.success) {
        setAdmins(response.data.admins || []);
      } else {
        setError(response.data.error || "Failed to load admin users");
      }
    } catch (err) {
      console.error("Error fetching admins:", err);
      console.error("Response:", err.response); // Debug log

      // More detailed error handling
      if (err.response?.status === 403) {
        setError("Access denied. Super admin privileges required.");
      } else if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
        navigate("/login");
      } else {
        setError(err.response?.data?.error || "Failed to load admin users");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch activity log
  const fetchActivityLog = async () => {
    try {
      const response = await axios.get("/api/admin/activity-log?limit=20");
      if (response.data.success) {
        setActivityLog(response.data.activities);
      }
    } catch (err) {
      console.error("Error fetching activity log:", err);
    }
  };

  // Create new admin
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateError("");

    // Validate passwords match
    if (newAdmin.password !== newAdmin.confirmPassword) {
      setCreateError("Passwords do not match");
      return;
    }

    // Validate password strength
    if (newAdmin.password.length < 8) {
      setCreateError("Password must be at least 8 characters");
      return;
    }

    try {
      setCreating(true);
      const response = await axios.post("/api/admin/create-admin", {
        username: newAdmin.username,
        email: newAdmin.email,
        password: newAdmin.password,
        is_super_admin: newAdmin.is_super_admin,
      });

      if (response.data.success) {
        alert("Admin user created successfully!");
        setShowCreateForm(false);
        setNewAdmin({
          username: "",
          email: "",
          password: "",
          confirmPassword: "",
          is_super_admin: false,
        });
        fetchAdmins();
        fetchActivityLog();
      }
    } catch (err) {
      setCreateError(err.response?.data?.error || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  // Toggle admin status
  const toggleAdminStatus = async (adminId, currentStatus) => {
    if (adminId === adminData.id) {
      alert("You cannot deactivate your own account");
      return;
    }

    const action = currentStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this admin?`)) {
      return;
    }

    setActionLoading({ ...actionLoading, [`toggle-${adminId}`]: true });

    try {
      const response = await axios.patch(`/api/admin/toggle-status/${adminId}`);
      if (response.data.success) {
        setSuccessMessage(
          response.data.message || `Admin ${action}d successfully`
        );
        fetchAdmins();
        fetchActivityLog();
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      alert(
        `Failed to ${action} admin: ${
          err.response?.data?.error || "Unknown error"
        }`
      );
    } finally {
      setActionLoading({ ...actionLoading, [`toggle-${adminId}`]: false });
    }
  };

  // Delete admin
  const deleteAdmin = async (adminId, username) => {
    if (adminId === adminData.id) {
      alert("You cannot delete your own account");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to permanently delete admin "${username}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setActionLoading({ ...actionLoading, [`delete-${adminId}`]: true });

    try {
      const response = await axios.delete(`/api/admin/delete/${adminId}`);
      if (response.data.success) {
        setSuccessMessage(
          response.data.message || "Admin deleted successfully"
        );
        fetchAdmins();
        fetchActivityLog();
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      alert(
        `Failed to delete admin: ${
          err.response?.data?.error || "Unknown error"
        }`
      );
    } finally {
      setActionLoading({ ...actionLoading, [`delete-${adminId}`]: false });
    }
  };

  // Reset admin password
  const resetAdminPassword = async (adminId, username) => {
    const newPassword = prompt(
      `Enter new password for ${username}:\n(Minimum 8 characters)`
    );
    if (!newPassword) return;

    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }

    setActionLoading({ ...actionLoading, [`reset-${adminId}`]: true });

    try {
      const response = await axios.post(
        `/api/admin/reset-password/${adminId}`,
        {
          newPassword,
        }
      );
      if (response.data.success) {
        setSuccessMessage(`Password reset successfully for ${username}`);
        alert(
          `Password reset successfully!\nNew password: ${newPassword}\n\nMake sure to share this securely with ${username}`
        );
        fetchActivityLog();
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      alert(
        `Failed to reset password: ${
          err.response?.data?.error || "Unknown error"
        }`
      );
    } finally {
      setActionLoading({ ...actionLoading, [`reset-${adminId}`]: false });
    }
  };

  // Change own password
  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    setPasswordError("");

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    // Validate password strength
    if (passwordData.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    try {
      setChangingPassword(true);
      const response = await axios.post("/api/admin/change-my-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.data.success) {
        alert("Password changed successfully!");
        setShowChangePassword(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        fetchActivityLog();
      }
    } catch (err) {
      setPasswordError(
        err.response?.data?.error || "Failed to change password"
      );
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Layout>
      {/* Admin Header */}
      <div className="bg-danger text-white py-2">
        <div className="container d-flex justify-content-between align-items-center">
          <div>
            <strong>🛡️ Admin Management</strong> - Super Admin Access Only
          </div>
          <div>
            <button
              onClick={() => navigate("/admin-dashboard")}
              className="btn btn-light btn-sm me-2"
            >
              Back to Dashboard
            </button>
            <button onClick={logout} className="btn btn-outline-light btn-sm">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Admin User Management</h1>
          <div>
            <button
              className="btn btn-warning me-2"
              onClick={() => setShowChangePassword(true)}
            >
              <i className="bi bi-key-fill me-2"></i>
              Change My Password
            </button>
            <button
              className="btn btn-success"
              onClick={() => setShowCreateForm(true)}
            >
              <i className="bi bi-person-plus-fill me-2"></i>
              Create New Admin
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div
            className="alert alert-success alert-dismissible fade show"
            role="alert"
          >
            <i className="bi bi-check-circle-fill me-2"></i>
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage("")}
            ></button>
          </div>
        )}

        {/* Change My Password Modal */}
        {showChangePassword && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-warning">
                  <h5 className="modal-title">
                    <i className="bi bi-key-fill me-2"></i>
                    Change My Password
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordError("");
                      setPasswordData({
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                    }}
                  ></button>
                </div>
                <form onSubmit={handleChangeMyPassword}>
                  <div className="modal-body">
                    {passwordError && (
                      <div className="alert alert-danger py-2" role="alert">
                        {passwordError}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Current Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            currentPassword: e.target.value,
                          })
                        }
                        required
                        disabled={changingPassword}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            newPassword: e.target.value,
                          })
                        }
                        required
                        disabled={changingPassword}
                        minLength="8"
                      />
                      <small className="text-muted">Minimum 8 characters</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            confirmPassword: e.target.value,
                          })
                        }
                        required
                        disabled={changingPassword}
                      />
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowChangePassword(false);
                        setPasswordError("");
                        setPasswordData({
                          currentPassword: "",
                          newPassword: "",
                          confirmPassword: "",
                        });
                      }}
                      disabled={changingPassword}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-warning"
                      disabled={changingPassword}
                    >
                      {changingPassword ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Changing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Change Password
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Create Admin Form Modal */}
        {showCreateForm && (
          <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-person-plus-fill me-2"></i>
                    Create New Admin User
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowCreateForm(false);
                      setCreateError("");
                      setNewAdmin({
                        username: "",
                        email: "",
                        password: "",
                        confirmPassword: "",
                        is_super_admin: false,
                      });
                    }}
                  ></button>
                </div>
                <form onSubmit={handleCreateAdmin}>
                  <div className="modal-body">
                    {createError && (
                      <div className="alert alert-danger py-2" role="alert">
                        {createError}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newAdmin.username}
                        onChange={(e) =>
                          setNewAdmin({ ...newAdmin, username: e.target.value })
                        }
                        required
                        disabled={creating}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={newAdmin.email}
                        onChange={(e) =>
                          setNewAdmin({ ...newAdmin, email: e.target.value })
                        }
                        required
                        disabled={creating}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={newAdmin.password}
                        onChange={(e) =>
                          setNewAdmin({ ...newAdmin, password: e.target.value })
                        }
                        required
                        disabled={creating}
                        minLength="8"
                      />
                      <small className="text-muted">Minimum 8 characters</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={newAdmin.confirmPassword}
                        onChange={(e) =>
                          setNewAdmin({
                            ...newAdmin,
                            confirmPassword: e.target.value,
                          })
                        }
                        required
                        disabled={creating}
                      />
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="superAdminCheck"
                        checked={newAdmin.is_super_admin}
                        onChange={(e) =>
                          setNewAdmin({
                            ...newAdmin,
                            is_super_admin: e.target.checked,
                          })
                        }
                        disabled={creating}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="superAdminCheck"
                      >
                        Grant Super Admin Privileges
                      </label>
                      <small className="d-block text-muted">
                        Super admins can create and manage other admin accounts
                      </small>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowCreateForm(false);
                        setCreateError("");
                      }}
                      disabled={creating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={creating}
                    >
                      {creating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Create Admin
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Admin Users Table */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              <i className="bi bi-people-fill me-2"></i>
              Admin Users ({admins.length})
            </h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin) => (
                      <tr key={admin.id}>
                        <td>
                          <strong>{admin.username}</strong>
                          {admin.id === adminData.id && (
                            <span className="badge bg-primary ms-2">You</span>
                          )}
                        </td>
                        <td>{admin.email}</td>
                        <td>
                          {admin.is_super_admin ? (
                            <span className="badge bg-danger">Super Admin</span>
                          ) : (
                            <span className="badge bg-secondary">Admin</span>
                          )}
                        </td>
                        <td>
                          {admin.is_active ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-warning">Inactive</span>
                          )}
                        </td>
                        <td>
                          {admin.last_login
                            ? new Date(admin.last_login).toLocaleDateString()
                            : "Never"}
                        </td>
                        <td>
                          {new Date(admin.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            {admin.id === adminData.id ? (
                              // Show special button for current user
                              <button
                                className="btn btn-warning"
                                title="Change My Password"
                                onClick={() => setShowChangePassword(true)}
                              >
                                <i className="bi bi-key-fill"></i> Change
                                Password
                              </button>
                            ) : (
                              // Show regular admin action buttons for other users
                              <>
                                <button
                                  className="btn btn-outline-warning"
                                  title="Reset Password"
                                  onClick={() =>
                                    resetAdminPassword(admin.id, admin.username)
                                  }
                                  disabled={actionLoading[`reset-${admin.id}`]}
                                >
                                  {actionLoading[`reset-${admin.id}`] ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-key"></i>
                                  )}
                                </button>
                                <button
                                  className={`btn ${
                                    admin.is_active
                                      ? "btn-outline-secondary"
                                      : "btn-outline-success"
                                  }`}
                                  title={
                                    admin.is_active
                                      ? "Deactivate Account"
                                      : "Activate Account"
                                  }
                                  onClick={() =>
                                    toggleAdminStatus(admin.id, admin.is_active)
                                  }
                                  disabled={actionLoading[`toggle-${admin.id}`]}
                                >
                                  {actionLoading[`toggle-${admin.id}`] ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i
                                      className={`bi ${
                                        admin.is_active
                                          ? "bi-pause-circle"
                                          : "bi-play-circle"
                                      }`}
                                    ></i>
                                  )}
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  title="Delete Admin"
                                  onClick={() =>
                                    deleteAdmin(admin.id, admin.username)
                                  }
                                  disabled={actionLoading[`delete-${admin.id}`]}
                                >
                                  {actionLoading[`delete-${admin.id}`] ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-trash"></i>
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Activity Log Section */}
        <div className="card shadow-sm">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-clock-history me-2"></i>
              Recent Admin Activity
            </h5>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setShowActivityLog(!showActivityLog);
                if (!showActivityLog) fetchActivityLog();
              }}
            >
              {showActivityLog ? "Hide" : "Show"} Activity
            </button>
          </div>
          {showActivityLog && (
            <div className="card-body">
              <div
                className="table-responsive"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Admin</th>
                      <th>Action</th>
                      <th>Details</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLog.map((activity) => (
                      <tr key={activity.id}>
                        <td className="text-nowrap">
                          {new Date(activity.created_at).toLocaleString()}
                        </td>
                        <td>{activity.username || "System"}</td>
                        <td>
                          <span
                            className={`badge ${
                              activity.action.includes("LOGIN")
                                ? "bg-primary"
                                : activity.action.includes("CREATE")
                                ? "bg-success"
                                : activity.action.includes("DELETE")
                                ? "bg-danger"
                                : activity.action.includes("UPDATE")
                                ? "bg-warning"
                                : "bg-secondary"
                            }`}
                          >
                            {activity.action}
                          </span>
                        </td>
                        <td>{activity.description}</td>
                        <td className="text-muted small">
                          {activity.ip_address}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminManagement;
